import { useState, useEffect } from 'react';
import { auth, db } from '../../../services/firebase';
import type { Payment, PaymentFilter } from '../../../types/payment.types';
import '../../../resources/paymentHistory.css';

/**
 * PaymentHistory Component
 * Displays a user's complete payment history with filtering and sorting capabilities
 *
 * Features:
 * - Loads payment history from Firebase
 * - Filters by payment status (all, succeeded, failed, refunded)
 * - Sorts by date (newest/oldest first)
 * - Displays payment details including card info and receipts
 */
export const PaymentHistory: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PaymentFilter>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  useEffect(() => {
    loadPaymentHistory();
  }, []);

  /**
   * Load payment history from Firebase
   */
  const loadPaymentHistory = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Fetch all payments for this user
      const paymentsRef = db.ref(`payments/${user.uid}`);
      const snapshot = await paymentsRef.once('value');
      const paymentsData = snapshot.val();

      if (!paymentsData) {
        setPayments([]);
        setLoading(false);
        return;
      }

      // Convert to array and sort by date (newest first by default)
      const paymentsArray: Payment[] = Object.values(paymentsData);
      paymentsArray.sort((a, b) => {
        const dateA = new Date(a.paidDate || a.createdAt).getTime();
        const dateB = new Date(b.paidDate || b.createdAt).getTime();
        return dateB - dateA; // Newest first
      });

      setPayments(paymentsArray);
      setLoading(false);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payments');
      setLoading(false);
    }
  };

  /**
   * Format currency from cents to dollars
   */
  const formatCurrency = (amountInCents: number): string => {
    return `$${(amountInCents / 100).toFixed(2)}`;
  };

  /**
   * Format ISO date to readable format
   */
  const formatDate = (isoDate: string): string => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /**
   * Get status badge with appropriate styling
   */
  const getStatusBadge = (status: string) => {
    const statusClass = `status-badge status-${status.toLowerCase()}`;
    return (
      <span className={statusClass}>
        {status}
      </span>
    );
  };

  /**
   * Filter payments based on selected filter
   */
  const filteredPayments = payments.filter(payment => {
    if (filter === 'all') return true;
    return payment.status === filter;
  });

  /**
   * Sort filtered payments based on sort order
   */
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    const dateA = new Date(a.paidDate || a.createdAt).getTime();
    const dateB = new Date(b.paidDate || b.createdAt).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Loading state
  if (loading) {
    return (
      <div className="payment-history">
        <div className="payment-history-loading">
          <div className="loading-spinner"></div>
          <p>Loading payment history...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="payment-history">
        <div className="payment-history-error">
          <p>Error: {error}</p>
          <button onClick={loadPaymentHistory} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-history">
      <div className="payment-history-header">
        <h2>Payment History</h2>

        <div className="payment-history-controls">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as PaymentFilter)}
            className="payment-filter"
          >
            <option value="all">All Payments</option>
            <option value="succeeded">Successful</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            className="payment-sort"
          >
            {sortOrder === 'desc' ? '↓ Newest First' : '↑ Oldest First'}
          </button>
        </div>
      </div>

      {sortedPayments.length === 0 ? (
        <div className="payment-history-empty">
          <div className="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2"/>
              <line x1="2" y1="10" x2="22" y2="10"/>
            </svg>
          </div>
          <h3>No payments found</h3>
          <p>
            {filter === 'all'
              ? "You haven't made any payments yet."
              : `No ${filter} payments found.`}
          </p>
        </div>
      ) : (
        <>
          <div className="payment-history-list">
            {sortedPayments.map((payment) => (
              <div key={payment.paymentId} className={`payment-card ${payment.status}`}>
                <div className="payment-card-header">
                  <div className="payment-card-date">
                    {formatDate(payment.paidDate || payment.createdAt)}
                    {payment.isLate && (
                      <span className="late-badge">
                        {payment.daysLate} {payment.daysLate === 1 ? 'day' : 'days'} late
                      </span>
                    )}
                  </div>
                  {getStatusBadge(payment.status)}
                </div>

                <div className="payment-card-body">
                  <div className="payment-card-amount">
                    {formatCurrency(payment.amount)}
                  </div>

                  <div className="payment-card-description">
                    {payment.description}
                  </div>

                  {payment.paymentMethod.brand && payment.paymentMethod.last4 && (
                    <div className="payment-card-method">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="card-icon">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                      <span className="payment-method-text">
                        <span style={{ textTransform: 'capitalize' }}>
                          {payment.paymentMethod.brand}
                        </span>
                        {' '}ending in {payment.paymentMethod.last4}
                      </span>
                    </div>
                  )}

                  {payment.failureMessage && (
                    <div className="payment-error-message">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="error-icon">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      {payment.failureMessage}
                    </div>
                  )}
                </div>

                {payment.receiptUrl && (
                  <div className="payment-card-footer">
                    <a
                      href={payment.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="receipt-link"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="receipt-icon">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                      </svg>
                      View Receipt
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="arrow-icon">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="payment-history-summary">
            <p>
              Showing {sortedPayments.length} of {payments.length} payment{payments.length !== 1 ? 's' : ''}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentHistory;
